import { useState, FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useBrand } from "../context/BrandContext";
import { useConfig } from "../context/ConfigContext";
import {
  colors, fontSizes, radii, transitions,
  centeredPageStyle, authCardStyle,
  baseInputStyle, baseInputFocusStyle,
  primaryBtnStyle, primaryBtnHoverStyle,
} from "../styles/theme";

const COGNITO_ENDPOINT = "https://cognito-idp.ap-southeast-2.amazonaws.com/";

async function cognitoForgotPassword(email: string, clientId: string) {
  const res = await fetch(COGNITO_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": "AWSCognitoIdentityProviderService.ForgotPassword",
    },
    body: JSON.stringify({ ClientId: clientId, Username: email }),
  });
  const body = await res.json();
  if (!res.ok) {
    const type: string = body.__type ?? "";
    // UserNotFoundException: fake success to prevent enumeration
    if (type === "UserNotFoundException") return;
    if (type === "LimitExceededException") throw new Error("LIMIT");
    throw new Error(body.message ?? `Request failed (${res.status})`);
  }
}

async function cognitoConfirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
  clientId: string,
) {
  const res = await fetch(COGNITO_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": "AWSCognitoIdentityProviderService.ConfirmForgotPassword",
    },
    body: JSON.stringify({
      ClientId: clientId,
      Username: email,
      ConfirmationCode: code,
      Password: newPassword,
    }),
  });
  const body = await res.json();
  if (!res.ok) {
    const type: string = body.__type ?? "";
    const msg: string  = body.message ?? `Reset failed (${res.status})`;
    if (type === "CodeMismatchException") throw new Error("CODE_MISMATCH");
    if (type === "ExpiredCodeException")  throw new Error("CODE_EXPIRED");
    if (type === "LimitExceededException") throw new Error("LIMIT");
    if (type === "InvalidPasswordException" || type === "InvalidParameterException") {
      throw new Error(`Password does not meet requirements: ${msg}`);
    }
    throw new Error(msg);
  }
}

type Stage = "request" | "reset";

function errorBanner(msg: string) {
  return (
    <div style={{
      background: "rgba(201,64,64,0.12)",
      border: `1px solid ${colors.error}`,
      borderRadius: radii.sm,
      padding: "0.6rem 0.9rem",
      color: colors.error,
      fontSize: fontSizes.sm,
    }}>
      {msg}
    </div>
  );
}

function infoBanner(msg: string) {
  return (
    <div style={{
      background: "rgba(76,175,136,0.10)",
      border: `1px solid ${colors.green}`,
      borderRadius: radii.sm,
      padding: "0.6rem 0.9rem",
      color: colors.green,
      fontSize: fontSizes.sm,
    }}>
      {msg}
    </div>
  );
}

export function FleetForgotPassword() {
  const navigate        = useNavigate();
  const brand           = useBrand();
  const { clientId }    = useConfig();

  const [stage,        setStage]        = useState<Stage>("request");
  const [email,        setEmail]        = useState("");
  const [code,         setCode]         = useState("");
  const [newPassword,  setNewPassword]  = useState("");
  const [confirm,      setConfirm]      = useState("");
  const [error,        setError]        = useState<string | null>(null);
  const [info,         setInfo]         = useState<string | null>(null);
  const [loading,      setLoading]      = useState(false);
  const [resending,    setResending]    = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [btnHover,     setBtnHover]     = useState(false);

  const inputStyle = (field: string) =>
    focusedField === field ? baseInputFocusStyle : baseInputStyle;

  const header = (
    <div style={{ textAlign: "center", marginBottom: "0.4rem" }}>
      <img
        src={brand.logo_url}
        alt={brand.name}
        style={{ height: 64, marginBottom: "0.75rem" }}
      />
      <h2 style={{
        color: brand.accent_color,
        fontSize: fontSizes.xl,
        fontWeight: 700,
        margin: 0,
        letterSpacing: "-0.01em",
      }}>
        {brand.name}
      </h2>
      <p style={{ color: colors.textMuted, fontSize: fontSizes.sm, margin: "0.25rem 0 0" }}>
        {stage === "request" ? "Reset your password" : "Set a new password"}
      </p>
    </div>
  );

  // ── Stage 1: request code ──────────────────────────────────────────────────

  async function handleRequest(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      await cognitoForgotPassword(email, clientId);
      setStage("reset");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Request failed";
      if (msg === "LIMIT") {
        setError("Too many attempts — please wait a moment and try again.");
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }

  // ── Stage 2: confirm reset ─────────────────────────────────────────────────

  async function handleReset(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (newPassword !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await cognitoConfirmForgotPassword(email, code.trim(), newPassword, clientId);
      navigate("/login", { state: { passwordReset: true } });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Reset failed";
      if (msg === "CODE_MISMATCH") {
        setError("Wrong code — check your email and try again.");
      } else if (msg === "CODE_EXPIRED") {
        setError("Code has expired.");
      } else if (msg === "LIMIT") {
        setError("Too many attempts — please wait a moment and try again.");
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setError(null);
    setInfo(null);
    setResending(true);
    try {
      await cognitoForgotPassword(email, clientId);
      setInfo("New code sent — check your inbox.");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Could not resend code";
      if (msg === "LIMIT") {
        setError("Too many attempts — please wait a moment and try again.");
      } else {
        setError(msg);
      }
    } finally {
      setResending(false);
    }
  }

  // ── Stage 2 render ─────────────────────────────────────────────────────────

  if (stage === "reset") {
    return (
      <div style={centeredPageStyle}>
        <form onSubmit={handleReset} style={authCardStyle}>
          {header}

          <div style={{ color: colors.textMuted, fontSize: fontSizes.sm, lineHeight: 1.6, display: "flex", flexDirection: "column", gap: 6 }}>
            <span>
              A 6-digit code has been sent if{" "}
              <strong style={{ color: colors.textBody }}>{email}</strong>{" "}
              matches a registered account. Check your inbox and spam folder.
            </span>
            <span style={{ fontSize: fontSizes.xs }}>
              Nothing after 60 s? Double-check the address you signed up with — we don't confirm which emails are registered.{" "}
              <button
                type="button"
                onClick={() => { setStage("request"); setError(null); setInfo(null); setCode(""); }}
                style={{ background: "none", border: "none", padding: 0, color: colors.goldSecondary, fontSize: "inherit", cursor: "pointer", textDecoration: "underline" }}
              >
                Try a different email
              </button>
            </span>
          </div>

          {error && errorBanner(error)}
          {info  && infoBanner(info)}

          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <label style={{ color: colors.textMuted, fontSize: fontSizes.sm, fontWeight: 500 }}>
              Verification code
            </label>
            <input
              type="text"
              inputMode="numeric"
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              required
              autoFocus
              onFocus={() => setFocusedField("code")}
              onBlur={() => setFocusedField(null)}
              style={{ ...inputStyle("code"), letterSpacing: "0.2em", textAlign: "center" }}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <label style={{ color: colors.textMuted, fontSize: fontSizes.sm, fontWeight: 500 }}>
              New password
            </label>
            <input
              type="password"
              placeholder="••••••••••"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              onFocus={() => setFocusedField("newpwd")}
              onBlur={() => setFocusedField(null)}
              style={inputStyle("newpwd")}
            />
            <span style={{ color: colors.textMuted, fontSize: fontSizes.xs }}>
              Min. 8 characters, with uppercase, number, and symbol.
            </span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <label style={{ color: colors.textMuted, fontSize: fontSizes.sm, fontWeight: 500 }}>
              Confirm new password
            </label>
            <input
              type="password"
              placeholder="••••••••••"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              onFocus={() => setFocusedField("confirm")}
              onBlur={() => setFocusedField(null)}
              style={{
                ...inputStyle("confirm"),
                ...(confirm && confirm !== newPassword
                  ? { border: `1px solid ${colors.error}` }
                  : {}),
              }}
            />
          </div>

          <button
            type="submit"
            disabled={loading || code.length < 6}
            onMouseEnter={() => setBtnHover(true)}
            onMouseLeave={() => setBtnHover(false)}
            style={{
              ...(btnHover && !loading ? primaryBtnHoverStyle : primaryBtnStyle),
              marginTop: "0.3rem",
              opacity: loading || code.length < 6 ? 0.7 : 1,
              cursor: loading || code.length < 6 ? "not-allowed" : "pointer",
              transition: transitions.fast,
            }}
          >
            {loading ? "Resetting…" : "Set new password"}
          </button>

          <div style={{ display: "flex", justifyContent: "center", gap: "0.5rem", flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              style={{
                background: "none",
                border: "none",
                color: colors.textMuted,
                fontSize: fontSizes.sm,
                cursor: resending ? "not-allowed" : "pointer",
                padding: 0,
                textDecoration: "underline",
                opacity: resending ? 0.6 : 1,
              }}
            >
              {resending ? "Sending…" : "Resend code"}
            </button>
            <span style={{ color: colors.textMuted, fontSize: fontSizes.sm }}>·</span>
            <Link
              to="/login"
              style={{ color: colors.textMuted, fontSize: fontSizes.sm, textDecoration: "underline" }}
            >
              Back to sign in
            </Link>
          </div>
        </form>
      </div>
    );
  }

  // ── Stage 1 render ─────────────────────────────────────────────────────────

  return (
    <div style={centeredPageStyle}>
      <form onSubmit={handleRequest} style={authCardStyle}>
        {header}

        <p style={{ color: colors.textMuted, fontSize: fontSizes.sm, margin: 0 }}>
          Enter your email and we'll send a reset code — if an account exists.
        </p>

        {error && errorBanner(error)}

        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <label style={{ color: colors.textMuted, fontSize: fontSizes.sm, fontWeight: 500 }}>
            Email
          </label>
          <input
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
            onFocus={() => setFocusedField("email")}
            onBlur={() => setFocusedField(null)}
            style={inputStyle("email")}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          onMouseEnter={() => setBtnHover(true)}
          onMouseLeave={() => setBtnHover(false)}
          style={{
            ...(btnHover && !loading ? primaryBtnHoverStyle : primaryBtnStyle),
            marginTop: "0.3rem",
            opacity: loading ? 0.7 : 1,
            cursor: loading ? "not-allowed" : "pointer",
            transition: transitions.fast,
          }}
        >
          {loading ? "Sending…" : "Send reset code"}
        </button>

        <p style={{ textAlign: "center", color: colors.textMuted, fontSize: fontSizes.sm, margin: 0 }}>
          <Link to="/login" style={{ color: colors.goldSecondary, textDecoration: "none" }}>
            Back to sign in
          </Link>
        </p>
      </form>
    </div>
  );
}
